import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ExternalLink, Eye, LifeBuoy, Mail, Pencil, Phone, Plus, Search, Trash2, Truck } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { EmptyState } from '../../../components/EmptyState';
import apiClient from '../../../lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/Dialog';
import { useAssetCatalogStore, type Vendor, type VendorInput } from '../../../store/assetCatalogStore';

const fieldClass =
  'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30';

const EMPTY: VendorInput = { name: '', email: '', contactPerson: '', contactEmail: '', phone: '', slaTerms: '', address: '', website: '', notes: '' };

const toInput = (v: Vendor): VendorInput => ({
  name: v.name, email: v.email || '', contactPerson: v.contactPerson || '', contactEmail: v.contactEmail || '',
  phone: v.phone || '', slaTerms: v.slaTerms || '', address: v.address || '', website: v.website || '', notes: v.notes || '',
});

/** Vendor directory — assets.suppliers via /assets/vendors. */
export function AssetVendors() {
  const { vendors, vendorsLoaded, fetchVendors, createVendor, updateVendor, deleteVendor } = useAssetCatalogStore();
  const [q, setQ] = useState('');
  const [editor, setEditor] = useState<{ id: number | null; form: VendorInput } | null>(null);
  const [deleting, setDeleting] = useState<Vendor | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewingLinked, setViewingLinked] = useState<{ vendor: Vendor; assets: any[]; purchaseOrders: any[]; tickets: any[] } | null>(null);
  const [loadingLinked, setLoadingLinked] = useState(false);

  const loadLinked = async (vendor: Vendor) => {
    setLoadingLinked(true);
    try {
      const res = await apiClient.get(`/assets/vendors/${vendor.id}/linked`);
      setViewingLinked(res.data?.data || { vendor, assets: [], purchaseOrders: [], tickets: [] });
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || 'Failed to load linked records');
    } finally {
      setLoadingLinked(false);
    }
  };

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? vendors.filter((v) => `${v.name} ${v.contactPerson} ${v.email}`.toLowerCase().includes(n)) : vendors;
  }, [vendors, q]);

  const save = async () => {
    if (!editor) return;
    if (!editor.form.name.trim()) { toast.error('Vendor name is required'); return; }
    const emailVal = (editor.form.email || '').trim();
    if (!emailVal) { toast.error('Company email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) { toast.error('Please enter a valid company email'); return; }
    setBusy(true);
    try {
      if (editor.id) {
        await updateVendor(editor.id, editor.form);
        toast.success('Vendor updated');
      } else {
        await createVendor(editor.form);
        toast.success('Vendor added');
      }
      setEditor(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await deleteVendor(deleting.id);
      toast.success(`${deleting.name} removed`);
      setDeleting(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof VendorInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    editor && setEditor({ ...editor, form: { ...editor.form, [k]: e.target.value } });

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        className="mb-0"
        icon={Truck}
        title="Vendors"
        description="Suppliers of your hardware, their contacts and support terms."
        actions={<Button onClick={() => setEditor({ id: null, form: EMPTY })}><Plus /> Add vendor</Button>}
      />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="flex items-center gap-2 font-display text-card-title text-foreground"><Truck className="size-4" /> {vendors.length} vendors</h2>
          <div className="w-full sm:w-72">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors" leftIcon={<Search className="size-4 text-muted-foreground" />} />
          </div>
        </div>
        {rows.length === 0 ? (
          <EmptyState className="m-4 border-0" title={vendorsLoaded ? (q ? 'No matching vendors' : 'No vendors yet') : 'Loading…'} description="Add the companies you buy hardware and support from." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-semibold">Vendor</th>
                  <th className="px-4 py-2 font-semibold">Contact</th>
                  <th className="px-4 py-2 font-semibold">Support terms</th>
                  <th className="px-4 py-2 text-right font-semibold">Assets</th>
                  <th className="px-4 py-2 text-right font-semibold">POs</th>
                  <th className="px-4 py-2 text-right font-semibold">Tickets</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((v) => (
                  <tr key={v.id} className="align-top hover:bg-secondary/30">
                    <td className="px-4 py-2">
                      <button type="button" onClick={() => loadLinked(v)} className="font-medium text-foreground hover:text-primary hover:underline text-left cursor-pointer">{v.name}</button>
                      {v.email && <p className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="size-3" /> {v.email}</p>}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      <p className="text-foreground">{v.contactPerson || '—'}</p>
                      {v.phone && <p className="flex items-center gap-1 text-xs"><Phone className="size-3" /> {v.phone}</p>}
                      {v.contactEmail && <p className="text-xs">{v.contactEmail}</p>}
                    </td>
                    <td className="max-w-xs px-4 py-2 text-muted-foreground">{v.slaTerms || '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">
                      <a href={`/assets/registry?search=${encodeURIComponent(v.name)}`} className="hover:text-primary hover:underline" title={`View assets for ${v.name}`}>
                        {v.assetCount}
                      </a>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">
                      <a href={`/assets/purchase-orders?search=${encodeURIComponent(v.name)}`} className="hover:text-primary hover:underline" title={`View purchase orders for ${v.name}`}>
                        {v.poCount}
                      </a>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-foreground">
                      <a href={`/support/tickets?search=${encodeURIComponent(v.name)}`} className="inline-flex items-center gap-1 hover:text-primary hover:underline" title={`View tickets for ${v.name}`}>
                        <span>{v.ticketCount ?? 0}</span>
                        {Boolean(v.openTicketCount && v.openTicketCount > 0) && (
                          <span className="rounded bg-amber-500/10 px-1 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                            {v.openTicketCount} open
                          </span>
                        )}
                      </a>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      <Button size="icon-sm" variant="ghost" aria-label={`View linked records for ${v.name}`} title="View linked records" onClick={() => loadLinked(v)}><Eye className="size-4" /></Button>
                      <Button size="icon-sm" variant="ghost" aria-label={`Edit ${v.name}`} onClick={() => setEditor({ id: v.id, form: toInput(v) })}><Pencil /></Button>
                      <Button size="icon-sm" variant="ghost" aria-label={`Delete ${v.name}`} onClick={() => setDeleting(v)}><Trash2 /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent side="right" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editor?.id ? 'Edit vendor' : 'Add vendor'}</DialogTitle>
            <DialogDescription>Company details and the support terms they provide.</DialogDescription>
          </DialogHeader>
          {editor && (
            <div className="grid gap-3 sm:grid-cols-2">
              <input className={`${fieldClass} sm:col-span-2`} placeholder="Company name *" value={editor.form.name} onChange={set('name')} maxLength={200} />
              <input className={fieldClass} placeholder="Company email *" type="email" value={editor.form.email} onChange={set('email')} />
              <input className={fieldClass} placeholder="Website" value={editor.form.website} onChange={set('website')} />
              <input className={fieldClass} placeholder="Contact person" value={editor.form.contactPerson} onChange={set('contactPerson')} />
              <input className={fieldClass} placeholder="Contact phone" value={editor.form.phone} onChange={set('phone')} />
              <input className={`${fieldClass} sm:col-span-2`} placeholder="Contact email" value={editor.form.contactEmail} onChange={set('contactEmail')} />
              <textarea className="min-h-16 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm sm:col-span-2" placeholder="Support / SLA terms"
                value={editor.form.slaTerms} onChange={set('slaTerms')} maxLength={1000} />
              <textarea className="min-h-16 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm sm:col-span-2" placeholder="Address"
                value={editor.form.address} onChange={set('address')} maxLength={1000} />
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditor(null)} disabled={busy}>Cancel</Button>
            <Button onClick={save} isLoading={busy}>{editor?.id ? 'Save changes' : 'Add vendor'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove {deleting?.name}?</DialogTitle>
            <DialogDescription>
              {deleting && (deleting.assetCount > 0 || deleting.poCount > 0)
                ? `This vendor is linked to ${deleting.assetCount} asset(s) and ${deleting.poCount} purchase order(s). Vendors with assets or open orders cannot be removed.`
                : 'The vendor will be removed from the directory.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleting(null)} disabled={busy}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} isLoading={busy} disabled={!!deleting && deleting.assetCount > 0}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Linked Records Dialog (Assets, POs, Tickets) */}
      <Dialog open={!!viewingLinked} onOpenChange={(o) => !o && setViewingLinked(null)}>
        <DialogContent side="right" className="max-w-2xl overflow-y-auto max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="size-5 text-primary" />
              <span>{viewingLinked?.vendor.name}</span>
            </DialogTitle>
            <DialogDescription>
              Connected records across Asset Management, Procurement, and Support.
            </DialogDescription>
          </DialogHeader>

          {viewingLinked && (
            <div className="space-y-4 py-2 text-sm">
              {/* Vendor quick contact summary */}
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-secondary/30 p-3 text-xs">
                <div>
                  <span className="text-muted-foreground block">Contact Person</span>
                  <span className="font-semibold text-foreground">{viewingLinked.vendor.contactPerson || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Company Email</span>
                  <span className="font-semibold text-foreground">{viewingLinked.vendor.email || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Contact Phone</span>
                  <span className="font-semibold text-foreground">{viewingLinked.vendor.phone || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block">SLA / Support Terms</span>
                  <span className="font-semibold text-foreground">{viewingLinked.vendor.slaTerms || '—'}</span>
                </div>
              </div>

              {/* Linked Assets */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <span>Linked Assets</span>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary font-bold">
                      {viewingLinked.assets.length}
                    </span>
                  </h4>
                  <a
                    href={`/assets/registry?search=${encodeURIComponent(viewingLinked.vendor.name)}`}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <span>View all in Registry</span>
                    <ExternalLink className="size-3" />
                  </a>
                </div>
                {viewingLinked.assets.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic rounded-md border border-dashed border-border p-3 text-center">
                    No assets linked to this vendor yet.
                  </p>
                ) : (
                  <div className="divide-y divide-border rounded-md border border-border bg-card max-h-40 overflow-y-auto">
                    {viewingLinked.assets.map((ast) => (
                      <div key={ast.id} className="flex items-center justify-between p-2 text-xs">
                        <div>
                          <span className="font-mono font-semibold text-primary mr-2">{ast.asset_code}</span>
                          <span className="text-foreground font-medium">{ast.name}</span>
                          <span className="text-muted-foreground ml-2">({ast.category || 'General'})</span>
                        </div>
                        <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-secondary text-secondary-foreground">
                          {ast.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Linked Purchase Orders */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <span>Purchase Orders</span>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary font-bold">
                      {viewingLinked.purchaseOrders.length}
                    </span>
                  </h4>
                  <a
                    href={`/assets/purchase-orders?search=${encodeURIComponent(viewingLinked.vendor.name)}`}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <span>View in PO Hub</span>
                    <ExternalLink className="size-3" />
                  </a>
                </div>
                {viewingLinked.purchaseOrders.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic rounded-md border border-dashed border-border p-3 text-center">
                    No purchase orders for this vendor.
                  </p>
                ) : (
                  <div className="divide-y divide-border rounded-md border border-border bg-card max-h-40 overflow-y-auto">
                    {viewingLinked.purchaseOrders.map((po) => (
                      <div key={po.id} className="flex items-center justify-between p-2 text-xs">
                        <div>
                          <span className="font-mono font-semibold text-foreground mr-2">{po.po_number}</span>
                          <span className="text-muted-foreground">{po.item}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono font-semibold text-foreground mr-2">{po.currency} {po.total_cost}</span>
                          <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-secondary text-secondary-foreground">
                            {po.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Linked Support Tickets */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <span>Support Tickets</span>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary font-bold">
                      {viewingLinked.tickets.length}
                    </span>
                  </h4>
                  <a
                    href={`/support/tickets?search=${encodeURIComponent(viewingLinked.vendor.name)}`}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <span>Open Support Hub</span>
                    <ExternalLink className="size-3" />
                  </a>
                </div>
                {viewingLinked.tickets.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic rounded-md border border-dashed border-border p-3 text-center">
                    No support tickets linked to this vendor.
                  </p>
                ) : (
                  <div className="divide-y divide-border rounded-md border border-border bg-card max-h-40 overflow-y-auto">
                    {viewingLinked.tickets.map((t) => (
                      <div key={t.id} className="flex items-center justify-between p-2 text-xs">
                        <div>
                          <span className="font-mono font-semibold text-primary mr-2">{t.ticket_no}</span>
                          <span className="text-foreground font-medium">{t.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px] text-muted-foreground">{t.priority}</span>
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${t.is_open ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' : 'bg-secondary text-secondary-foreground'}`}>
                            {t.is_open ? 'Open' : 'Closed'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="secondary" onClick={() => setViewingLinked(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}